import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import {
    afterNextRender,
    Component,
    effect,
    inject,
    Input,
    OnInit,
    PLATFORM_ID,
    Renderer2,
    signal,
    WritableSignal,
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import {
    NavigationEnd,
    NavigationStart,
    Router,
    RouterOutlet,
} from '@angular/router';
import {
    BabylonFooterContactI,
    BabylonFooterLinksI,
    BabylonFooterLogosI,
    BabylonGlobal,
    BabylonGlobalModalI,
    BabylonHeaderBookShowI,
    BabylonHeaderClearI,
    BabylonHeaderMenuCenterI,
    BabylonHeaderMenuLogoCenterI,
    BabylonHeaderMenuShowI,
    BabylonLanguageModalI,
    BabylonMenuButtonI,
    BabylonModalMultipleMotorsI,
    BabylonNewsletterI,
    BabylonStaticFooterI,
    BabylonStaticFooterV2I,
    BodyComponent,
    MapperService,
    PageConfig,
    Utils,
} from 'ctt-babylon';
import { catchError, filter, forkJoin, map, of, shareReplay, tap } from 'rxjs';
import { LayoutComponent } from './components/layout/layout.component';
import { BodyPageTypeClassDirective } from './directives/pageType/page-type.directive';
import { environment } from './environments/environment';
import { ApiService } from './services/api/api.service';
import { DataInfoService } from './services/data/data-info.service';
import { LayoutService } from './services/layout/layout.service';
import { LinkService } from './services/link.service';
import { RoutesService } from './services/routes/routes.service';
import { TEMPLATES } from './services/routes/templates';
import { SiteConfigService } from './services/site-config/site-config.service';

type GLOBAL_COMPONENTS =
    | BabylonMenuButtonI
    | BabylonLanguageModalI
    | BabylonStaticFooterI
    | BabylonNewsletterI
    | BabylonFooterLinksI
    | BabylonFooterLogosI
    | BabylonStaticFooterV2I;

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [
        CommonModule,
        RouterOutlet,
        LayoutComponent,
        BodyPageTypeClassDirective,
    ],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
    private static readonly NON_CRITICAL_THIRD_PARTY_HOSTS = [
        'ad.doubleclick.net',
        'cdn.cookie-script.com',
        'opticksprotection.com',
        'p.relay-t.io',
        'cdn.denomatic.com',
        'customs.affilired.com',
        'widget.hijiffy.com',
        'service.hijiffy.com',
        'api.hijiffy.com',
        'settings.luckyorange.com',
        'www.googletagmanager.com',
        'www.google-analytics.com',
    ];
    @Input() fuentesGoogleValidas: string[] = [];
    private readonly platformId = inject(PLATFORM_ID);
    private readonly document = inject(DOCUMENT);
    private lastGlobalDataRequestKey = '';
    private lastCommonScriptsKey = '';

    isHomePage = signal(false);
    header: WritableSignal<
        | BabylonHeaderBookShowI
        | BabylonHeaderClearI
        | BabylonHeaderMenuCenterI
        | BabylonHeaderMenuLogoCenterI
        | BabylonHeaderMenuShowI
        | undefined
    > = signal(undefined);

    languages: WritableSignal<BabylonLanguageModalI | undefined> =
        signal(undefined);
    newsletter: WritableSignal<BabylonNewsletterI | undefined> =
        signal(undefined);
    newsletterv2: WritableSignal<BabylonGlobalModalI | undefined> =
        signal(undefined);
    offerPopupV3: WritableSignal<BabylonGlobalModalI | undefined> =
        signal(undefined);
    modalMultipleMotors: WritableSignal<
        BabylonModalMultipleMotorsI | undefined
    > = signal(undefined);
    globalInfo: WritableSignal<BabylonGlobal | undefined> = signal(undefined);

    selectroModal: WritableSignal<any | undefined> = signal(undefined);

    private staticFooter: BodyComponent<
        BabylonStaticFooterI | BabylonStaticFooterV2I
    > = {} as BodyComponent<BabylonStaticFooterI | BabylonStaticFooterV2I>;

    private foC0SvgLink: BodyComponent<
        BabylonStaticFooterI | BabylonStaticFooterV2I
    > = {} as BodyComponent<BabylonStaticFooterI | BabylonStaticFooterV2I>;

    private footerLogos: BodyComponent<BabylonFooterLogosI> =
        {} as BodyComponent<BabylonFooterLogosI>;

    private footerLinks: BodyComponent<BabylonFooterLinksI> =
        {} as BodyComponent<BabylonFooterLinksI>;

    private foC3ImgTxt: BodyComponent<BabylonFooterLinksI> =
        {} as BodyComponent<BabylonFooterLinksI>;

    private folislinks: BodyComponent<BabylonFooterLinksI> =
        {} as BodyComponent<BabylonFooterLinksI>;

    private foc3txticov2: BodyComponent<BabylonFooterLinksI> =
        {} as BodyComponent<BabylonFooterLinksI>;

    constructor(
        private readonly apiService: ApiService,
        private readonly siteSvc: SiteConfigService,
        private readonly mapperSvc: MapperService,
        private readonly layoutSvc: LayoutService,
        private readonly renderer: Renderer2,
        private readonly routesSvc: RoutesService,
        private router: Router,
        private readonly dataInfoService: DataInfoService,
        private readonly titleService: Title,
        private readonly meta: Meta,
        private readonly linkService: LinkService
    ) {
        afterNextRender(() => {
            if (!isPlatformBrowser(this.platformId)) return;
            this.initBrowserOnly();
        });

        effect(() => {
            const loaded = this.layoutSvc.firstPageLoaded();
            if (loaded) {
                this.setPageHeadConfig();
                this.updateHtmlLang();
            }
        });

        this.router.events
            .pipe(filter((ev) => ev instanceof NavigationEnd))
            .subscribe(() => {
                const pageId = this.routesSvc.getPageId();
                this.isHomePage.set(
                    this.siteSvc.pages?.find(
                        (p) => p.id === pageId || 'P-' + p.id === pageId
                    )?.home === 1 ||
                        this.siteSvc.pages?.find(
                            (p) => p.id === pageId || 'P-' + p.id === pageId
                        )?.dynamicHeader == true
                );
                this.setPageHeadConfig();
                const cfg = this.siteSvc.webConfigInstant;
                if (cfg) this.setCommonScripts(cfg);
            });
    }

    private initBrowserOnly(): void {
        const cfg = this.siteSvc.webConfigInstant;
        if (cfg) {
            if (cfg.favIcons?.length) this.setFavicon();
            this.setCommonScripts(cfg);

            let iconsUrl = (cfg?.css as any).icons as string;

            // Si hay url de iconos y NO es una URL absoluta (http/https), concatenar environment.api
            if (iconsUrl && !/^https?:\/\//i.test(iconsUrl)) {
                // 1. Tomamos la apiBaseUrl del environment
                // 2. Quitamos el "/api" (y su posible barra final) si existe al final de la cadena
                // 3. Por si acaso, limpiamos cualquier otra barra final ('/') que pudiera quedar
                const baseUrl = environment.apiBaseUrl
                    ? environment.apiBaseUrl
                          .replace(/\/api\/?$/i, '')
                          .replace(/\/$/, '')
                    : '';

                // Concatenamos asegurando una única barra en medio
                iconsUrl = baseUrl + '/' + iconsUrl.replace(/^\//, '');
            }

            this.loadExternalIcons(iconsUrl);
        }
        // Redirección desde raíz según localStorage
        this.handleRootRedirection();

        this.getDataApi();

        this.routesSvc.addRoutingEvents();

        let isRedirecting = false;
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart && /[A-Z]/.test(event.url)) {
                const fixedURL = environment.normalizeUrlsToLowercase
                    ? event.url.toLocaleLowerCase()
                    : event.url;
                if (event.url !== fixedURL) {
                    isRedirecting = true;
                    this.router
                        .navigateByUrl(fixedURL, { replaceUrl: true })
                        .then(() => {
                            isRedirecting = false;
                        });
                }
            }
        });
    }

    ngOnInit(): void {
        this.injectGoogleFontsFromVariables();
        this.updateHtmlLang();
        const pageId = this.routesSvc.getPageId();
        this.isHomePage.set(
            this.siteSvc.pages?.find(
                (p) => p.id === pageId || 'P-' + p.id === pageId
            )?.home === 1 ||
                this.siteSvc.pages?.find(
                    (p) => p.id === pageId || 'P-' + p.id === pageId
                )?.dynamicHeader == true
        );
        // dont delete, used for SSR to work
        if (!isPlatformBrowser(this.platformId)) {
            this.getDataApi();
        }
    }

    private handleRootRedirection(): void {
        // Ya no necesitamos esta función aquí porque la redirección
        // se maneja correctamente en routes.service.ts con la configuración de rutas
        // Mantenemos el método vacío por compatibilidad pero ya no hace nada
    }

    private updateHtmlLang(): void {
        const selectedLang = this.siteSvc.selectedLanguageInstant;
        if (selectedLang?.locate) {
            const htmlElement = this.document.querySelector('html');
            if (htmlElement) {
                this.renderer.setAttribute(
                    htmlElement,
                    'lang',
                    selectedLang.locate
                );
            }
        }
    }

    getDataApi() {
        const isBrowser = isPlatformBrowser(this.platformId);

        const requestKey = this.buildGlobalDataRequestKey();

        if (isBrowser && requestKey === this.lastGlobalDataRequestKey) {
            return;
        }

        if (isBrowser) {
            this.lastGlobalDataRequestKey = requestKey;
        }

        const globalComponents$ = this.siteSvc
            .getPageConfig<
                GLOBAL_COMPONENTS | undefined
            >(this.routesSvc.getPageIdByTemplate(TEMPLATES.GLOBAL_COMPONENTS), 0)
            .pipe(
                map((response) =>
                    response instanceof HttpResponse ? response.body : response
                ),
                filter(
                    (
                        config
                    ): config is PageConfig<GLOBAL_COMPONENTS | undefined> =>
                        config !== null
                ),
                tap((response) => this.manageFooterInfo(response)),
                catchError(() => of(undefined)),
                shareReplay(1)
            );

        const engine$ = !environment.booking_engine_widget
            ? this.apiService
                  .getEngine(
                      this.siteSvc.selectedLanguageInstant?.id,
                      'site',
                      environment.siteId
                  )
                  .pipe(
                      filter((data) => data !== null),
                      map((data) => Utils.convertObjectKeysToCamelCase(data)),
                      map((data) => this.mapperSvc.mapEngine(data)),
                      tap((data) => this.layoutSvc.setEngineData(data)),
                      catchError(() => of(undefined))
                  )
            : of(undefined);

        forkJoin([globalComponents$, engine$])
            .pipe(
                tap(([globalComponents]) => {
                    if (!globalComponents) return;

                    const body = (globalComponents as any).body || [];
                    const bodyExtra = (globalComponents as any).bodyExtra || [];

                    const langModal =
                        Utils.findComponent(body, 'languagesmodal') ??
                        (Utils.findComponent(
                            body,
                            'languagesmodalv2'
                        ) as BabylonLanguageModalI) ??
                        (Utils.findComponent(
                            body,
                            'languagemodalv3'
                        ) as BabylonLanguageModalI);

                    this.languages.set(
                        this.mapperSvc.mapLanguageModal(langModal)
                    );

                    this.newsletter.set(
                        this.mapperSvc.mapNewsletterModal(
                            Utils.findComponent(
                                body,
                                'newslettermodal'
                            ) as BabylonNewsletterI
                        )
                    );

                    this.newsletterv2.set(
                        this.mapperSvc.mapGlobals(
                            Utils.findComponent(body, 'newslettermodalv2') as
                                | BabylonGlobalModalI
                                | undefined,
                            undefined,
                            undefined
                        )
                    );

                    if (this.newsletterv2()) {
                        this.dataInfoService.setNewsletterDataV2(
                            this.newsletterv2()
                        );
                    }

                    this.offerPopupV3.set(
                        this.mapperSvc.mapGlobals(
                            Utils.findComponent(body, 'offerpopupv3') as
                                | BabylonGlobalModalI
                                | undefined,
                            undefined,
                            undefined
                        )
                    );

                    if (this.offerPopupV3()) {
                        this.dataInfoService.setOfferPopupV3Data(
                            this.offerPopupV3()
                        );
                    }

                    this.modalMultipleMotors.set(
                        this.mapperSvc.mapModalMultipleMotors(
                            Utils.findComponent(
                                body,
                                'modalmultiplemotors'
                            ) as BabylonModalMultipleMotorsI
                        )
                    );

                    this.header.set(
                        this.mapperSvc.mapHeader(
                            Utils.findComponent(bodyExtra, 'headerbuttons'),
                            Utils.findComponent(
                                body,
                                'nav'
                            ) as BabylonMenuButtonI,
                            Utils.findComponent(body, 'c4metxtsvgcta') //TODO change it to a enum
                        )
                    );

                    this.selectroModal.set(
                        this.mapperSvc.mapGlobals(
                            Utils.findComponent(body, 'modc2imgtxtcta') as
                                | any
                                | undefined,
                            undefined,
                            undefined
                        )
                    );

                    if (this.selectroModal()) {
                        this.dataInfoService.setSelectroModalData(
                            this.selectroModal()
                        );
                    }

                    this.globalInfo.set(
                        this.mapperSvc.mapGlobals(
                            Utils.findComponent(body, 'contentglobalinfo') as
                                | any
                                | undefined,
                            undefined,
                            undefined
                        )
                    );
                }),
                catchError((err) => {
                    if (
                        isBrowser &&
                        this.lastGlobalDataRequestKey === requestKey
                    ) {
                        this.lastGlobalDataRequestKey = '';
                    }

                    console.error('❌ Error cargando datos globales:', err);
                    return of([undefined, undefined]);
                })
            )
            .subscribe();
    }

    private buildGlobalDataRequestKey(): string {
        const globalPageId =
            this.routesSvc.getPageIdByTemplate(TEMPLATES.GLOBAL_COMPONENTS) ??
            'global';
        const langId = this.siteSvc.selectedLanguageInstant?.id ?? 'lang';
        const currentRoute =
            this.router.url ||
            this.document.location?.pathname ||
            this.document.location?.href ||
            '/';

        return [globalPageId, langId, currentRoute].join('|');
    }

    manageFooterInfo(
        response: PageConfig<GLOBAL_COMPONENTS | undefined>
    ): void {
        const footer = response.body.find(
            (component) =>
                component.name?.toLowerCase()?.includes('staticfooter') ||
                component.name?.toLowerCase()?.includes('staticfooterv4') ||
                component.name?.toLowerCase()?.includes('c4folinksvg') ||
                component.name?.toLowerCase()?.includes('folisc4txt') ||
                component.name?.toLowerCase()?.includes('foc3txtsvgcta') ||
                component.name?.toLowerCase()?.includes('foc3txticov2')
        ) as BodyComponent<any>;

        if (footer) {
            this.staticFooter = footer;
            this.layoutSvc.setFooter({
                ...this.staticFooter,
                props: this.staticFooter?.name?.toLowerCase()?.includes('v2')
                    ? this.mapperSvc.mapStaticFooterv2(footer.props)
                    : this.staticFooter?.name?.toLowerCase()?.includes('v3') ||
                        this.staticFooter?.name
                            ?.toLowerCase()
                            ?.includes('c4folinksvg') ||
                        this.staticFooter?.name
                            ?.toLowerCase()
                            ?.includes('folisc4txt') ||
                        this.staticFooter?.name
                            ?.toLowerCase()
                            ?.includes('staticfooterv4')
                      ? this.mapperSvc.mapGlobals(footer, undefined, undefined)
                      : this.mapperSvc.mapStaticFooter(footer.props),
            });
        }

        const footer2 = response.body.find((component) =>
            component.name?.toLowerCase()?.includes('foc0svglink')
        ) as BodyComponent<any>;

        if (footer2) {
            this.foC0SvgLink = footer2;
            this.layoutSvc.setFoC0SvgLinkData({
                ...this.foC0SvgLink,
                props: this.mapperSvc.mapGlobals(footer2, undefined, undefined),
            });
        }

        const foC3ImgTxt = response.body.find((component) =>
            component.name?.toLowerCase()?.includes('foc3imgtxt')
        ) as BodyComponent<any>;

        if (foC3ImgTxt) {
            this.foC3ImgTxt = foC3ImgTxt;
            this.layoutSvc.setFoC3ImgTxtData({
                ...this.foC3ImgTxt,
                props: this.foC3ImgTxt?.name
                    ?.toLowerCase()
                    ?.includes('foc3imgtxt')
                    ? this.mapperSvc.mapGlobals(
                          foC3ImgTxt,
                          undefined,
                          undefined
                      )
                    : this.mapperSvc.mapStaticFooter(foC3ImgTxt.props),
            });
        }

        const foC3TxtIcoV2 = response.body.find((component) =>
            component.name?.toLowerCase()?.includes('foc3txticov2')
        ) as BodyComponent<any>;

        if (foC3TxtIcoV2) {
            this.foc3txticov2 = foC3TxtIcoV2;
            this.layoutSvc.setFoC3TxtIcoV2Data({
                ...foC3TxtIcoV2,
                props: this.mapperSvc.mapGlobals(
                    foC3TxtIcoV2,
                    undefined,
                    undefined
                ),
            });
        }

        const folislinks = response.body.find((component) =>
            component.name?.toLowerCase()?.includes('lislinkclean')
        ) as BodyComponent<BabylonFooterLinksI>;

        if (folislinks) {
            this.folislinks = folislinks;
            this.layoutSvc.setFolislinksData({
                ...this.folislinks,
                props: this.mapperSvc.mapGlobals(
                    folislinks,
                    undefined,
                    undefined
                ),
            });
        }

        const footerLogos = response.body.find((component) =>
            component.name?.toLowerCase()?.includes('footerlogos')
        ) as BodyComponent<BabylonFooterLogosI>;

        if (footerLogos) {
            this.footerLogos = footerLogos;
            this.layoutSvc.setFooterLogosData({
                ...this.footerLogos,
                props: this.mapperSvc.mapFooterLogos(footerLogos.props),
            });
        }

        const footerLinks = response.body.find((component) =>
            component.name?.toLowerCase()?.includes('footerlinks')
        ) as BodyComponent<BabylonFooterLinksI>;

        if (footerLinks) {
            this.footerLinks = footerLinks;
            this.layoutSvc.setFooterLinksData({
                ...this.footerLinks,
                props: this.mapperSvc.mapFooterLinks(footerLinks.props),
            });
        }

        const footerContact = response.body.find((component) =>
            component.name?.toLowerCase()?.includes('footercontact')
        ) as BodyComponent<BabylonFooterContactI>;

        if (footerContact) {
            this.layoutSvc.setFooterContactData({
                ...footerContact,
                props: this.mapperSvc.mapFooterContact(footerContact.props),
            });
        }
    }

    manageInitialConfig(): void {
        // La lógica se ha movido a un effect en el constructor para que sea reactiva.
    }

    loadExternalIcons(suffixUrl?: string | null): void {
        if (isPlatformBrowser(this.platformId)) {
            if (suffixUrl) {
                Utils.loadStyle$(suffixUrl).subscribe();
            }
        }
    }

    setFavicon(): void {
        if (isPlatformBrowser(this.platformId)) {
            document.querySelectorAll("link[rel='icon']").forEach((favicon) => {
                this.renderer.removeChild(document.head, favicon);
            });

            const favIcons = this.siteSvc.webConfigInstant?.favIcons;
            const faviconUrl =
                (favIcons && favIcons.find(Boolean)?.toString()) ||
                'assets/babylon/favicons/favicon.ico';

            const newFavicon = this.renderer.createElement('link');
            this.renderer.setAttribute(newFavicon, 'rel', 'icon');
            this.renderer.setAttribute(newFavicon, 'type', 'image/x-icon');
            this.renderer.setAttribute(newFavicon, 'href', faviconUrl);

            this.renderer.appendChild(document.head, newFavicon);
        }
    }

    setCommonScripts(config: any) {
        if (isPlatformBrowser(this.platformId)) {
            const gtms: string[] = Array.isArray(config.commonScripts?.GTM)
                ? config.commonScripts.GTM.filter((id: string) => !!id).map(
                      (id: string) => id.trim().toUpperCase()
                  )
                : [];
            const pageScripts = this.isHomePage()
                ? (config.scripts ?? [])
                      .map((raw: any) =>
                          typeof raw === 'string' ? raw : raw?.script
                      )
                      .filter(Boolean)
                : [];
            const currentKey = JSON.stringify({
                route:
                    this.router.url ||
                    this.document.location?.pathname ||
                    this.document.location?.href ||
                    '/',
                home: this.isHomePage(),
                gtms,
                pageScripts,
            });

            if (currentKey === this.lastCommonScriptsKey) {
                return;
            }

            this.lastCommonScriptsKey = currentKey;

            document
                .querySelectorAll('script[data-from-config]')
                .forEach((el) => this.renderer.removeChild(document.head, el));

            if (this.isHomePage()) {
                (config.scripts ?? []).forEach((raw: any) =>
                    this.addSafeScript(raw, 'from-config')
                );
            }

            if (!environment.hideUserCentrics) {
                gtms.forEach((gtmId) => this.injectGtm(gtmId));
            } else {
                console.log(
                    '🛑 [Dev] GTM bloqueado para evitar la inyección de Usercentrics.'
                );
            }
        }
    }

    setPageHeadConfig(): void {
        const config = this.layoutSvc.headInstant;
        if (!config) return;

        // 1. Obtenemos el ID del idioma que el usuario tiene activo ahora mismo
        const currentLangId = this.siteSvc.selectedLanguageInstant?.id;

        // 2. Filtramos para quedarnos SOLO con las metas de su idioma (o las que no tengan idioma asignado por si son globales)
        const filteredMetas = config.metas?.filter(
            (meta: any) =>
                meta.language_id === currentLangId || !meta.language_id
        );

        // 3. Creamos un nuevo objeto config solo con las metas correctas
        const configToApply = {
            ...config,
            metas: filteredMetas,
        };

        // 4. Se lo pasamos a las funciones de renderizado
        if (isPlatformBrowser(this.platformId)) {
            this.setPageHeadConfigBrowser(configToApply);
        } else {
            this.setPageHeadConfigSSR(configToApply);
        }
    }

    private setPageHeadConfigSSR(config: any): void {
        if (!config || !config.metas) return;

        const headElement = this.document.head;

        // --- 🛡️ EL GUARDIA DE SEGURIDAD ---
        // Si encontramos aunque sea UN meta con nuestra marca, significa que esta
        // es la 2ª o 3ª llamada. Como queremos quedarnos con el primero, abortamos aquí.
        if (headElement.querySelector('[data-ssr-injected="true"]')) {
            return;
        }

        // 1. LIMPIEZA: Borramos metas estáticos del index.html base
        const tagsToRemove = headElement.querySelectorAll(
            'meta:not([charset]):not([name="viewport"]), link[rel="canonical"], link[rel="alternate"]'
        );
        tagsToRemove.forEach((tag) => headElement.removeChild(tag));

        // 2. APLANAR DATOS:
        // Vital para evitar el error "metas.forEach is not a function" porque
        // tu primer JSON es un Objeto con arrays dentro, no un Array puro.
        let allMetas: any[] = [];
        if (Array.isArray(config.metas)) {
            allMetas = [...config.metas];
        } else {
            Object.keys(config.metas).forEach((key) => {
                const item = config.metas[key];
                if (Array.isArray(item)) {
                    allMetas = [...allMetas, ...item];
                } else if (typeof item === 'object' && item !== null) {
                    allMetas.push(item);
                }
            });
        }

        // 3. TÍTULO: Se actualiza solo
        const titleTag = allMetas.find(
            (m: any) => m.type === 'title' || m.name === 'title'
        );
        if (titleTag && titleTag.content) {
            this.titleService.setTitle(titleTag.content);
        }

        // 4. CONSTRUCCIÓN DINÁMICA
        let fullHeadHtml = '';
        allMetas.forEach((enlace: any) => {
            if (
                enlace['type'] === 'title' ||
                enlace['name'] === 'title' ||
                enlace['name'] === 'viewport' ||
                enlace['name']?.toLowerCase() === 'charset' ||
                enlace['charset']
            )
                return;

            const tagName = enlace['type'] === 'link' ? 'link' : 'meta';
            let tagStr = `<${tagName}`;

            // 👉 AÑADIMOS LA MARCA INVISIBLE (Esto es lo que frena las llamadas 2 y 3)
            tagStr += ` data-ssr-injected="true"`;

            // Recorremos todas las claves
            Object.keys(enlace).forEach((key) => {
                if (
                    !['group', 'type', 'id', 'charset'].includes(key) &&
                    enlace[key] !== null &&
                    enlace[key] !== undefined
                ) {
                    // Escapamos por seguridad para SSR (Hotel & Spa -> Hotel &amp; Spa)
                    const safeValue = String(enlace[key])
                        .replace(/&/g, '&amp;')
                        .replace(/"/g, '&quot;');
                    tagStr += ` ${key}="${safeValue}"`;
                }
            });

            tagStr += '>';
            fullHeadHtml += tagStr;
        });

        // --- 4.5. INYECCIÓN DE SCHEMAS EN SSR ---
        if (config.scripts && Array.isArray(config.scripts)) {
            config.scripts.forEach((rawScript: any) => {
                const scriptStr =
                    typeof rawScript === 'string'
                        ? rawScript
                        : rawScript.script;

                if (!scriptStr) return;

                // Comprobamos si ya es un script o si es un JSON puro
                if (scriptStr.includes('application/ld+json')) {
                    fullHeadHtml += scriptStr;
                } else {
                    const looksLikeJson =
                        scriptStr.trim().startsWith('{') ||
                        scriptStr.trim().startsWith('[');
                    if (looksLikeJson) {
                        // Lo envolvemos manualmente para el SSR
                        fullHeadHtml += `<script type="application/ld+json">${scriptStr}</script>`;
                    }
                }
            });
        }
        /*          FIX si lo de arriba no funciona, sustituir
        // --- 4.5. INYECCIÓN DE SCHEMAS EN SSR ---
        if (config.scripts && Array.isArray(config.scripts)) {
            config.scripts.forEach((rawScript: any) => {
                const scriptStr =
                    typeof rawScript === 'string'
                        ? rawScript.trim()
                        : rawScript.script ? rawScript.script.trim() : '';

                if (!scriptStr) return;

                // CASO 1: Viene con la etiqueta HTML <script> incluida (Imagen 1)
                if (scriptStr.startsWith('<script') && scriptStr.includes('application/ld+json')) {
                    fullHeadHtml += scriptStr;
                } 
                // CASO 2: Viene como JSON puro (Imagen 2)
                else if (scriptStr.startsWith('{') || scriptStr.startsWith('[')) {
                    fullHeadHtml += `<script type="application/ld+json">\n${scriptStr}\n</script>`;
                }
            });
        } */

        // 5. INYECCIÓN FÍSICA
        try {
            headElement.insertAdjacentHTML('afterbegin', fullHeadHtml);
        } catch (e) {
            console.error('❌ [SSR] Error en la inyección física:', e);
        }
    }

    private setPageHeadConfigBrowser(config: any): void {
        // Browser: Manipulación DOM completa
        this.document
            .querySelectorAll('script[data-from-page-config]')
            .forEach((el) => this.renderer.removeChild(this.document.head, el));

        (config.scripts ?? [])
            .map((s: any) => (typeof s === 'string' ? s : s.script))
            .filter(Boolean)
            .forEach((raw: string) =>
                this.addSafeScript(raw, 'from-page-config')
            );

        const links = this.document.querySelectorAll('link');
        links.forEach((link) => {
            if (
                link.getAttribute('rel') === 'canonical' ||
                link.getAttribute('rel') === 'alternate'
            ) {
                this.renderer.removeChild(this.document.head, link);
            }
        });

        const metas = this.document.querySelectorAll('meta');
        metas.forEach((meta) => {
            if (
                meta.getAttribute('charset') !== 'utf-8' &&
                meta.getAttribute('name') !== 'viewport' &&
                meta.getAttribute('name') !== 'description'
            ) {
                this.renderer.removeChild(this.document.head, meta);
            }
        });

        this.removeHeadComments();

        let group = '';

        config?.metas?.forEach((enlace: any) => {
            if (enlace['group'] !== group) {
                this.renderer.appendChild(
                    this.document.head,
                    this.renderer.createComment(enlace['group'].toUpperCase())
                );
                group = enlace['group'];
            }
            let linkElement: any;
            const metaDescription = this.document.querySelector(
                'meta[name="description"]'
            );
            switch (enlace['type']) {
                case 'link':
                    linkElement = this.renderer.createElement(
                        'link'
                    ) as HTMLLinkElement;
                    break;
                case 'meta':
                    if (enlace['name'] === 'description' && metaDescription) {
                        this.renderer.setAttribute(
                            metaDescription,
                            'content',
                            enlace['content']
                        );
                        break;
                    }
                    linkElement = this.renderer.createElement(
                        'meta'
                    ) as HTMLMetaElement;
                    break;
                case 'title':
                    linkElement = this.document.querySelector('title');
                    linkElement.text = enlace['content'];
                    break;

                default:
                    break;
            }
            if (
                (enlace['type'] !== 'title' &&
                    enlace['name'] !== 'description') ||
                (enlace['name'] === 'description' && metaDescription === null)
            ) {
                Object.keys(enlace).forEach((key) => {
                    if (key !== 'group' && key !== 'type') {
                        this.renderer.setAttribute(
                            linkElement,
                            key,
                            enlace[key]
                        );
                    }
                });
            }

            if (
                (enlace['name'] !== 'viewport' &&
                    enlace['type'] !== 'title' &&
                    enlace['name'] !== 'charset' &&
                    enlace['name'] !== 'description') ||
                (enlace['name'] === 'description' &&
                    metaDescription === null &&
                    linkElement)
            ) {
                this.renderer.appendChild(this.document.head, linkElement);
            }
        });
    }

    private addSafeScript(
        raw: string | { script: string },
        dataAttr: string
    ): void {
        const html = (typeof raw === 'string' ? raw : raw.script).trim();

        if (
            environment.hideUserCentrics &&
            (html.includes('uc.js') || html.includes('usercentrics'))
        ) {
            console.log(
                '🛑 [Dev] Script de Usercentrics bloqueado para evitar lag en consola.'
            );
            return; // Cortamos la ejecución aquí, el script no se inyecta.
        }

        const scriptEl = this.renderer.createElement('script');

        if (html.startsWith('<script')) {
            const tmp = this.document.createElement('div');
            tmp.innerHTML = html;
            const original = tmp.querySelector('script')!;

            Array.from(original.attributes).forEach((attr) =>
                this.renderer.setAttribute(scriptEl, attr.name, attr.value)
            );
            scriptEl.textContent = original.textContent;
        } else {
            const looksLikeJson = html.startsWith('{') || html.startsWith('[');
            this.renderer.setAttribute(
                scriptEl,
                'type',
                looksLikeJson ? 'application/ld+json' : 'text/javascript'
            );
            scriptEl.textContent = html;
        }

        this.renderer.setAttribute(scriptEl, `data-${dataAttr}`, 'true');

        if (this.shouldDeferThirdPartyScript(scriptEl)) {
            this.deferScriptInjection(scriptEl);
            return;
        }

        this.appendScriptIfMissing(scriptEl);
    }

    private shouldDeferThirdPartyScript(scriptEl: HTMLScriptElement): boolean {
        const src = scriptEl.getAttribute('src');
        if (!src) return false;

        try {
            const resolvedUrl = new URL(src, this.document.location?.origin);
            return AppComponent.NON_CRITICAL_THIRD_PARTY_HOSTS.some((host) =>
                resolvedUrl.hostname.includes(host)
            );
        } catch {
            return false;
        }
    }

    private deferScriptInjection(scriptEl: HTMLScriptElement): void {
        const inject = () => this.appendScriptIfMissing(scriptEl);
        const requestIdle = (window as any).requestIdleCallback as
            | ((cb: () => void, opts?: { timeout: number }) => number)
            | undefined;

        if (requestIdle) {
            requestIdle(inject, { timeout: 3000 });
            return;
        }

        window.setTimeout(inject, 1200);
    }

    private appendScriptIfMissing(scriptEl: HTMLScriptElement): void {
        const src = scriptEl.getAttribute('src');
        if (src) {
            const normalized = new URL(
                src,
                this.document.location?.origin
            ).toString();
            const exists = Array.from(
                this.document.querySelectorAll('script[src]')
            ).some((node) => {
                try {
                    return (
                        new URL(
                            node.getAttribute('src') ?? '',
                            this.document.location?.origin
                        ).toString() === normalized
                    );
                } catch {
                    return false;
                }
            });

            if (exists) return;

            // Ayuda a que no bloquee parse/paint cuando se inserta dinámicamente
            scriptEl.async = true;
            scriptEl.defer = true;
        }

        // Insertar antes del primer <style> o <link rel="stylesheet"> en el head
        const head = this.document.head;
        const firstStyleOrLink = head.querySelector(
            'style, link[rel="stylesheet"]'
        );
        if (firstStyleOrLink) {
            head.insertBefore(scriptEl, firstStyleOrLink);
        } else {
            this.renderer.appendChild(head, scriptEl);
        }
    }

    private injectGtm(gtmId: string): void {
        const existingGtmScript = Array.from(
            this.document.head.querySelectorAll('script')
        ).some((script) => (script.textContent ?? '').includes(`GTM-${gtmId}`));

        const existingGtmNoscript = Array.from(
            this.document.body.querySelectorAll('noscript iframe')
        ).some((iframe) =>
            (iframe.getAttribute('src') ?? '').includes(`GTM-${gtmId}`)
        );

        if (existingGtmScript || existingGtmNoscript) {
            return;
        }

        const gtm = this.renderer.createElement('script');
        gtm.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','GTM-${gtmId}');`;
        this.renderer.appendChild(
            this.document.head,
            this.renderer.createComment('Google Tag Manager')
        );
        this.renderer.appendChild(this.document.head, gtm);
        this.renderer.appendChild(
            this.document.head,
            this.renderer.createComment('End Google Tag Manager')
        );

        const ns = this.renderer.createElement('noscript');
        ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-${gtmId}"
      height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
        this.renderer.appendChild(
            this.document.body,
            this.renderer.createComment('Google Tag Manager (noscript)')
        );
        this.renderer.appendChild(this.document.body, ns);
        this.renderer.appendChild(
            this.document.body,
            this.renderer.createComment('End Google Tag Manager (noscript)')
        );
    }

    removeHeadComments() {
        if (isPlatformBrowser(this.platformId)) {
            const head = document.head;
            const treeWalker = document.createTreeWalker(
                head,
                NodeFilter.SHOW_COMMENT,
                null
            );

            const comments = [];

            while (treeWalker.nextNode()) {
                comments.push(treeWalker.currentNode);
            }

            for (const comment of comments) {
                comment?.parentNode?.removeChild(comment);
            }
        }
    }

    injectGoogleFontsFromVariables(): void {
        if (isPlatformBrowser(this.platformId)) {
            const computedStyles = getComputedStyle(
                this.document.documentElement
            );
            const fontVariables = [
                '--font-title',
                '--font-pretitle',
                '--font-text',
                '--font-claim',
            ];

            fontVariables.forEach((varName) => {
                const value = computedStyles.getPropertyValue(varName).trim();
                const match = value.match(/^['"]?([^'",]+)['"]?/);
                const fontName = match ? match[1] : null;

                if (
                    fontName &&
                    this.fuentesGoogleValidas.includes(fontName) // solo si es fuente de Google
                ) {
                    const formattedFont = fontName.replace(/ /g, '+');
                    const id = `font-${formattedFont}`;

                    if (!this.document.getElementById(id)) {
                        const link = this.document.createElement('link');
                        link.id = id;
                        link.rel = 'stylesheet';
                        link.href = `https://fonts.googleapis.com/css2?family=${formattedFont}&display=swap`;
                        this.document.head.appendChild(link);
                    }
                }
            });
        }
    }
}
